/**
 * Renders the device input audit log, over the `audit` data channel.
 *
 * The phone streams captured text entries as JSON lines: its stored history first, then live
 * entries as input happens. Each entry is one field's settled value with the app, screen and
 * time it was entered. Password and other secure fields arrive redacted (contents stripped by
 * Android, kept only as a length), and are shown as such.
 *
 * Wire format, matching the phone side (InputAuditLog / ScreenShareService):
 * - Down, text: one entry per frame, `{"time":…,"app":…,"label":…,"screen":…,"field":…,"text":…,"redacted":…,"len":…}`
 * - Up, text: `{"c":"clear"}` to wipe the log on the device
 */

export interface AuditEntry {
  /** Epoch milliseconds, from the device clock. */
  time: number;
  /** Package name of the app the text went into. */
  app: string;
  /** Human app label, when it could be resolved. */
  label?: string;
  /** Foreground screen (activity/dialog) at the time. */
  screen?: string;
  /** The field: its hint, description, or view id. */
  field?: string;
  /** The settled field text. Empty when redacted. */
  text: string;
  /** True for a password or other secure field: contents are never captured. */
  redacted: boolean;
  /** Character count, including for redacted fields. */
  len: number;
}

interface Callbacks {
  onCount?: (n: number) => void;
}

export class AuditLog {
  private channel: RTCDataChannel | null = null;
  private readonly entries: AuditEntry[] = [];
  private lastTime = -1;

  constructor(
    private readonly list: HTMLElement,
    private readonly callbacks: Callbacks = {},
  ) {}

  attach(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.onmessage = (event) => {
      if (typeof event.data === 'string') this.ingest(event.data);
    };
  }

  /**
   * Dumps the current entries to a downloaded file. Two files, so the log is useful both to
   * read and to process: a readable .txt and the raw .jsonl. Redacted (password) fields stay
   * redacted in the dump, the same as on screen.
   */
  export(): void {
    if (this.entries.length === 0) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    this.download(`input-log-${stamp}.txt`, 'text/plain', this.asText());
    this.download(`input-log-${stamp}.jsonl`, 'application/x-ndjson', this.asJsonl());
  }

  private asText(): string {
    const header = `# droid-remote input log — exported ${new Date().toISOString()} — ${this.entries.length} entries\n`;
    const lines = this.entries.map((e) => {
      const when = new Date(e.time).toISOString();
      const app = e.label ? `${e.label} (${e.app})` : e.app;
      const where = [e.screen, e.field].filter(Boolean).join(' / ');
      const value = e.redacted ? `[password redacted · ${e.len} chars]` : e.text;
      return [when, app, where, value].join('\t');
    });
    return header + lines.join('\n') + '\n';
  }

  private asJsonl(): string {
    return this.entries.map((e) => JSON.stringify(e)).join('\n') + '\n';
  }

  private download(name: string, type: string, body: string): void {
    const url = URL.createObjectURL(new Blob([body], { type }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Wipes the log on the device and clears the local view. */
  clear(): void {
    if (this.channel?.readyState === 'open') this.channel.send(JSON.stringify({ c: 'clear' }));
    this.entries.length = 0;
    this.lastTime = -1;
    this.list.replaceChildren();
    this.callbacks.onCount?.(0);
  }

  dispose(): void {
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel = null;
    }
    this.entries.length = 0;
    this.lastTime = -1;
  }

  private ingest(text: string): void {
    let entry: AuditEntry;
    try {
      entry = JSON.parse(text) as AuditEntry;
    } catch {
      return;
    }
    if (typeof entry.time !== 'number') return;
    this.insert(entry);
    this.callbacks.onCount?.(this.entries.length);
  }

  /**
   * Inserts keeping the list ordered by time, newest at the bottom. The common case (history
   * streamed oldest-first, then live entries) is a plain append; an out-of-order arrival (a live
   * entry that raced the history backfill) triggers a one-off re-render, which is rare and cheap.
   */
  private insert(entry: AuditEntry): void {
    if (entry.time >= this.lastTime) {
      this.entries.push(entry);
      this.lastTime = entry.time;
      const atBottom = this.isScrolledToBottom();
      this.list.append(this.row(entry));
      if (atBottom) this.list.scrollTop = this.list.scrollHeight;
      return;
    }
    // Out of order: place it and rebuild.
    let i = this.entries.length;
    while (i > 0 && this.entries[i - 1]!.time > entry.time) i--;
    this.entries.splice(i, 0, entry);
    this.render();
  }

  private render(): void {
    this.list.replaceChildren(...this.entries.map((e) => this.row(e)));
    this.list.scrollTop = this.list.scrollHeight;
  }

  private isScrolledToBottom(): boolean {
    return this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight < 24;
  }

  private row(entry: AuditEntry): HTMLElement {
    const row = document.createElement('div');
    row.className = 'audit-row';
    row.setAttribute('role', 'listitem');

    const time = document.createElement('span');
    time.className = 'audit-time';
    const date = new Date(entry.time);
    time.textContent = date.toLocaleTimeString(undefined, { hour12: false });
    time.title = date.toLocaleString();

    const app = document.createElement('span');
    app.className = 'audit-app';
    app.textContent = entry.label ?? shortPackage(entry.app);
    app.title = entry.app;

    const where = document.createElement('span');
    where.className = 'audit-where';
    where.textContent = [entry.screen, entry.field].filter(Boolean).join(' · ');
    where.title = where.textContent;

    const text = document.createElement('span');
    text.className = 'audit-text';
    if (entry.redacted) {
      text.classList.add('redacted');
      text.textContent = `🔒 password · ${entry.len} char${entry.len === 1 ? '' : 's'}`;
    } else {
      text.textContent = entry.text;
    }

    row.append(time, app, where, text);
    return row;
  }
}

/** The last dotted segment of a package name, when there is no resolved app label. */
function shortPackage(pkg: string): string {
  const parts = pkg.split('.');
  return parts[parts.length - 1] || pkg;
}
